var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * CZNA-1518 — the outcome of the form's last check, as counts in the pane header: a dot
 * with the errors, a triangle with the warnings. Clicking it opens the check panel.
 *
 * It replaced a banner above the form, which on a small screen pushed the fields down and
 * stayed there while the cataloguer was fixing them. The counts take one line, and the list
 * is a click away.
 *
 * The form does not check while it is being filled in, so once something is typed the
 * counts describe values that are gone. They then turn grey and say "zkontrolovat": the
 * click runs the check again before the panel opens, so the numbers only ever change when
 * the cataloguer asks for it.
 */
import React from 'react';
import { useIntl } from 'react-intl';
var COLOR = {
    error: '#b4261f',
    warning: '#8a5d00',
    ok: '#2f7a3a',
    stale: '#5d6570',
};
var Dot = function (_a) {
    var color = _a.color;
    return (React.createElement("span", { "aria-hidden": "true", style: { width: '8px', height: '8px', borderRadius: '50%', background: color, display: 'inline-block' } }));
};
// A triangle, not just another colour — the two must not differ by hue alone.
var Triangle = function (_a) {
    var color = _a.color;
    return (React.createElement("span", { "aria-hidden": "true", style: {
            width: 0,
            height: 0,
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderBottom: "9px solid ".concat(color),
            display: 'inline-block',
        } }));
};
export var CheckBadge = React.forwardRef(function (_a, ref) {
    var errorCount = _a.errorCount, warningCount = _a.warningCount, stale = _a.stale, expanded = _a.expanded, controls = _a.controls, onClick = _a.onClick;
    var intl = useIntl();
    var summary = intl.formatMessage({ id: 'ui-czna-authorities.validation.badge.label' }, { errors: errorCount, warnings: warningCount });
    var label = stale
        ? "".concat(summary, ". ").concat(intl.formatMessage({ id: 'ui-czna-authorities.validation.badge.staleHint' }))
        : summary;
    var clean = errorCount === 0 && warningCount === 0;
    var count = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        fontWeight: 600,
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("style", null, '.czna-check-badge:hover{border-color:#0073e6 !important}'),
        React.createElement("button", { ref: ref, type: "button", onClick: onClick, "aria-expanded": expanded, "aria-controls": controls, "aria-label": label, title: label, "data-test-check-badge": true, "data-test-check-errors": errorCount, "data-test-check-warnings": warningCount, "data-test-check-stale": stale || undefined, className: "czna-check-badge", style: {
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                padding: '3px 10px',
                borderRadius: '99px',
                border: "1px ".concat(stale ? 'dashed' : 'solid', " ").concat(expanded ? '#0073e6' : '#d9dde3'),
                background: expanded ? '#eef0f3' : '#fff',
                color: stale ? COLOR.stale : '#1f2329',
                font: 'inherit',
                fontSize: '0.93em',
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
            } },
            errorCount > 0 && (React.createElement("span", { style: __assign(__assign({}, count), { color: stale ? COLOR.stale : COLOR.error }) },
                React.createElement(Dot, { color: stale ? COLOR.stale : COLOR.error }),
                errorCount)),
            warningCount > 0 && (React.createElement("span", { style: __assign(__assign({}, count), { color: stale ? COLOR.stale : COLOR.warning }) },
                React.createElement(Triangle, { color: stale ? COLOR.stale : COLOR.warning }),
                warningCount)),
            clean && (React.createElement("span", { style: __assign(__assign({}, count), { color: stale ? COLOR.stale : COLOR.ok }) },
                React.createElement("span", { "aria-hidden": "true" }, "\u2713"),
                intl.formatMessage({ id: 'ui-czna-authorities.validation.badge.clean' }))),
            stale && (React.createElement("span", { "aria-hidden": "true", style: { fontSize: '0.9em' } },
                '↻ ',
                intl.formatMessage({ id: 'ui-czna-authorities.validation.badge.recheck' }))))));
});
CheckBadge.displayName = 'CheckBadge';
export default CheckBadge;
